import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTextSize } from '../utils/textSize';

/**
 * The Getting Started guide and the captioned walkthroughs, shown inside Help & Support. They are the same public pages
 * the login screen and the footer link to (public/guide), opened in a frame so nobody loses their place in the app; the pages
 * notice they are framed and drop their own sign-in links (see the script at the top of each).
 */

export const WALKTHROUGHS = [
  {
    key: 'quote-to-cash',
    label: 'Quotation to cash',
    about: 'From quotation to cash in hand, in 16 captioned screens: quote a customer, send it, bill it, and see the payment reach the books.',
  },
  {
    key: 'set-up-your-items',
    label: 'Set up your items',
    about: 'Setting up your items, in 9 screens: add a material from the Global Material Master with the stock you already hold, then a product.',
  },
  {
    key: 'sell-online',
    label: 'Sell online',
    about: 'Selling online, in 21 screens: set up your store, payments and shipping, publish products, a buyer orders, and your staff invoice, send, deliver, take a return and read the store report.',
  },
];

/** The guide, as tall as its own text so the page scrolls once, not twice. */
export function GuideFrame() {
  const { theme } = useTheme();
  const textSize = useTextSize();
  const frame = useRef(null);
  const [height, setHeight] = useState(900);

  const fit = useCallback(() => {
    try {
      const doc = frame.current?.contentDocument;
      if (doc?.documentElement) setHeight(Math.max(doc.documentElement.scrollHeight, 400));
    } catch (e) {
      // A frame we cannot read keeps its last height
    }
  }, []);

  useEffect(() => {
    const onResize = () => fit();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fit]);

  const onLoad = () => {
    fit();
    try {
      const doc = frame.current?.contentDocument;
      if (doc?.body && typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(fit).observe(doc.body);
      }
    } catch (e) {
      // Fitting once on load is enough if the page cannot be watched
    }
  };

  return (
    <iframe
      ref={frame}
      title="Getting Started guide"
      src={`/guide/getting-started.html?theme=${theme}&size=${textSize}`}
      onLoad={onLoad}
      className="w-100 border rounded"
      style={{ height, display: 'block' }}
      data-testid="guide-frame"
    />
  );
}

/** The walkthroughs: pick one, watch it in place. The address can name one (?story=sell-online). */
export function WalkthroughFrame() {
  const [params, setParams] = useSearchParams();
  const asked = params.get('story');
  const active = WALKTHROUGHS.find((w) => w.key === asked) || WALKTHROUGHS[0];

  const pick = (key) => setParams({ story: key }, { replace: true });

  return (
    <div>
      <Nav variant="pills" className="mb-2 gap-1" activeKey={active.key} onSelect={pick} aria-label="Walkthroughs">
        {WALKTHROUGHS.map((w) => (
          <Nav.Item key={w.key}>
            <Nav.Link eventKey={w.key} data-testid={`walkthrough-${w.key}`}>{w.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <p className="text-muted small mb-2">{active.about}</p>
      <iframe
        key={active.key}
        title={active.label}
        src={`/guide/walkthroughs/${active.key}/index.html`}
        className="w-100 border rounded"
        style={{ height: 'max(32rem, calc(100vh - 17rem))', display: 'block' }}
        data-testid="walkthrough-frame"
      />
    </div>
  );
}
